import { cn } from '@/lib/utils'
import type { IncidentsPage } from '@/lib/api/deep-dive.types'
import { formatDateTime, formatRelative } from '@/lib/ws-status'
import {
  CATEGORIES,
  CATEGORY_ORDER,
  incidentCategory,
  incidentDuration,
  incidentHttpStatus,
  plainCause,
  summarizeIncidents,
  windowLabel,
  type IncidentWindow,
} from '@/lib/platform-incidents'

/** The slice of a query result this card reads. */
export interface IncidentsQueryState {
  data?: IncidentsPage
  isLoading: boolean
  isError: boolean
}

interface PlatformIncidentSummaryProps {
  name: string
  period: IncidentWindow
  query?: IncidentsQueryState
}

/** Hover card for one platform: how often it broke lately, and what happened last. */
export function PlatformIncidentSummary({ name, period, query }: PlatformIncidentSummaryProps) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-semibold">{name}</p>
        <p className="text-fg-subtle font-mono text-[10px] uppercase">{windowLabel(period)}</p>
      </div>
      <SummaryBody query={query} period={period} />
    </div>
  )
}

function SummaryBody({ query, period }: { query?: IncidentsQueryState; period: IncidentWindow }) {
  if (!query || query.isLoading) return <p className="text-fg-muted">Loading incidents…</p>
  if (query.isError || !query.data) return <p className="text-fg-muted">Couldn't load incidents.</p>

  const { total, byCategory, last } = summarizeIncidents(query.data)
  if (total === 0 || !last) {
    return <p className="text-healthy">No incidents in the {windowLabel(period)}.</p>
  }

  const category = CATEGORIES[incidentCategory(last)]
  const code = incidentHttpStatus(last)
  return (
    <>
      <p>
        <span className="font-mono text-base font-semibold">{total}</span>{' '}
        <span className="text-fg-muted">incident{total === 1 ? '' : 's'}</span>
      </p>
      <ul className="space-y-1">
        {CATEGORY_ORDER.filter((c) => byCategory[c]).map((c) => (
          <li key={c} className="flex items-center gap-2">
            <span className={cn('size-1.5 shrink-0 rounded-full', CATEGORIES[c].dot)} />
            <span className="text-fg-muted flex-1">{CATEGORIES[c].label}</span>
            <span className="font-mono">{byCategory[c]}</span>
          </li>
        ))}
      </ul>
      <div className="border-line space-y-1 border-t pt-2">
        <p className="text-fg-subtle font-mono text-[10px] uppercase">
          {last.ongoing ? 'Happening now' : 'Last incident'}
        </p>
        <p>
          <span className={cn('font-semibold', category.text)}>{category.label}</span>
          <span className="text-fg-muted">
            {' '}
            · {formatDateTime(last.started_at)} ({formatRelative(last.started_at)}) ·{' '}
            {incidentDuration(last)}
          </span>
        </p>
        <p className="text-fg-muted line-clamp-2">{plainCause(last)}</p>
        {code && <p className="text-fg-subtle font-mono text-[10px]">HTTP {code}</p>}
      </div>
      <p className="text-fg-subtle text-[10px]">Full history: All Incidents, top right.</p>
    </>
  )
}
